import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNav } from '../App';
import {
  Box, Typography, Paper, Button, Card, CircularProgress,
  Chip, Alert, TextField, Dialog, DialogTitle, DialogContent, DialogActions,
  Select, MenuItem, FormControl, InputLabel, RadioGroup,
  FormControlLabel, Radio, Divider, Snackbar
} from '@mui/material';
import axios from 'axios';
import ChatGameCard from '../ChatGameCard';
import { useGameInvite } from '../useGameSession';
import { GAME_REGISTRY } from '../games/registry';
import { C, SP } from '../theme';

import { QUESTION_TEMPLATES, QUICK_REPLIES, IDENTITY_ACTIONS, GAME_INVITES } from './chat/chatConstants';
import { resolveUIState } from './chat/resolveUIState';
import { SystemMessage, UserBubble, QABlock, QuestionChip, BucketDisplay } from './chat/components/MessageBubble';
import ChatHeader from './chat/components/ChatHeader';
import GameInviteCard from './chat/components/GameInviteCard';
import SocialAskBar, { HandleInput } from './chat/components/SocialAskBar';
import useChatPoll from './chat/hooks/useChatPoll';
import useSocialExchange from './chat/hooks/useSocialExchange';
import useChatActions from './chat/hooks/useChatActions';
import { useFeedbackTrigger } from './chat/feedback';
import useGameActions from './chat/hooks/useGameActions';

const API_BASE = process.env.REACT_APP_API_BASE || 'https://api.binko.me/api';

// ============================================================
// HELPERS
// ============================================================
function formatTime(s) {
  if (s <= 0) return 'Expired';
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

function getMatchIdFromPath() {
  const parts = window.location.pathname.split('/').filter(Boolean);
  const idx = parts.indexOf('chat');
  return (idx !== -1 && parts[idx + 1]) ? parts[idx + 1] : (parts[1] || null);
}





// ============================================================
// MAIN COMPONENT
// ============================================================
function ChatConversation() {
  const navigate = useNav();
  const fromGame = new URLSearchParams(window.location.search).get('from') === 'game';

  const { triggerWeMet, triggerUnmatch, triggerDeleteChat, triggerReport } = useFeedbackTrigger({ navigate });

  const [serverState, setServerState] = useState(null);
  const [loading, setLoading]         = useState(true);
  const [chatClosed, setChatClosed]   = useState(false);
  const [error, setError]             = useState('');
  const [activeSection, setActiveSection] = useState(null);
  const [inputFocused, setInputFocused] = useState(false);

  const [freeText, setFreeText]               = useState('');
  const [selectedAnswer, setSelectedAnswer]   = useState('');
  const [identityAction, setIdentityAction]   = useState('');
  const [showSpotDialog, setShowSpotDialog]   = useState(false);
  const [manHint, setManHint]                 = useState(false);
  const [noThanksOpen, setNoThanksOpen]       = useState(false);

  const [proposing,     setProposing]     = useState(false);
  const [gameError,     setGameError]     = useState('');
  const trayRef = useRef(null);
  const { proposeGame, acceptInvite, rejectInvite, blocked } = useGameInvite();

  const matchId     = getMatchIdFromPath();
  const token       = localStorage.getItem('binko_token');

  const { fetchAll, isFetching, messagesEnd, sectionEnd, exitedSessionRef, messagesContainer } = useChatPoll({
    matchId, token, activeSection,
    setServerState, setChatClosed, setLoading,
    messageCount: serverState?.messages?.length ?? 0,
    activeGameSessionFsmState: serverState?.activeGameSession?.fsm_state,
  });

  const ui = useMemo(() => resolveUIState(serverState), [serverState]);

  // ── DERIVED GAME STATE (server-authoritative) ──
  function derivePlayUiState(activeGameSession, myUserId) {
    if (!activeGameSession) return 'idle';
    const { fsm_state, invited_by, invited_player } = activeGameSession;
    if (exitedSessionRef.current && exitedSessionRef.current === activeGameSession.id) return 'idle';
    if (fsm_state === 'INVITED' && invited_player === myUserId) return 'responding';
    if (fsm_state === 'INVITED' && invited_by === myUserId) return 'waiting';
    if (fsm_state === 'READY_PENDING') return 'ready';
    if (fsm_state === 'ACTIVE') return 'playing';
    if (fsm_state === 'COMPLETED' && activeGameSession?.end_reason === 'resign') return 'playing';
    if (fsm_state === 'COMPLETED') return 'playing';
    return 'idle';
  }

  const activeGameSession = serverState?.activeGameSession ?? null;
  const myUserId          = serverState?.myUserId ?? null;
  const playUiState       = derivePlayUiState(activeGameSession, myUserId);
  const gameSessionId     = activeGameSession?.id ?? null;
  const selectedGame      = activeGameSession?.game_type ?? null;

  const {
    call,
    sendQuestion, sendAnswer, sendQuickReply, sendFreeText,
    initiateSpotting, openSpotDialog, submitSpotAction, confirmSpotting,
    skipToChat, requestBuffer,
    askToMeet, respondToMeet, confirmMeeting,
  } = useChatActions({
    matchId, token, fetchAll, isFetching, setError,
    setActiveSection, setSelectedAnswer, setFreeText,
    setIdentityAction, setShowSpotDialog, setChatClosed,
    freeText,
  });

  const { handle, setHandle, platform, setPlatform,
          handleShareAndAsk, handleAskOnly, handleShareOnly, handleDecline
        } = useSocialExchange({ call });

  const {
    proposeGameAndWait, acceptAndPlay, declineGame,
    resetToGameSelection, handleGameRestart,
  } = useGameActions({
    call, fetchAll, matchId,
    proposing, setProposing, setGameError, setActiveSection,
    proposeGame, acceptInvite, rejectInvite,
    activeGameSession, gameSessionId, exitedSessionRef,
    serverState,
  });

  // ── AUTO-OPEN GAMES TAB WHEN GAME NEEDS ATTENTION ──
  // Opens the Games tab whenever the game enters a state that requires
  // the player to see the game card: ready, playing, or opponent resigned.
  // Covers the inviting player who never called acceptAndPlay.
  useEffect(() => {
    if (!activeGameSession) return;
    if (exitedSessionRef.current === activeGameSession.id) return; // I exited this session
    if (playUiState === 'ready' || playUiState === 'playing') {
      setActiveSection('P');
    }
  }, [playUiState, activeGameSession?.id]);

  // ── MESSAGE RENDERER ──
  const renderMessage = (msg) => {
    const { myUserId, otherUser } = serverState || {};
    const isMe = msg.sender_id === myUserId;
    const alias = isMe ? 'You' : (otherUser?.alias || 'Them');

    switch (msg.message_type) {
      case 'system':
        return <SystemMessage key={msg.id} text={msg.system_message} />;
      case 'identity':
        return <UserBubble key={msg.id} text={msg.system_message} isMe={isMe} senderAlias={alias} />;
      case 'quick_reply':
        return <UserBubble key={msg.id} text={msg.system_message} isMe={isMe} senderAlias={alias} />;
      case 'free_text': {
        const gameType = GAME_INVITES[msg.system_message];
        if (gameType && !isMe) {
          return (
            <GameInviteCard
              key={msg.id}
              msg={msg}
              gameType={gameType}
              playUiState={playUiState}
              activeGameSession={activeGameSession}
              serverState={serverState}
              acceptAndPlay={acceptAndPlay}
              declineGame={declineGame}
            />
          );
        }
        return <UserBubble key={msg.id} text={msg.system_message} isMe={isMe} senderAlias={alias} />;
      }
      case 'question':
        if (msg.question_key === 'meet_request')
          return <QuestionChip key={msg.id} text={msg.system_message || 'Would you like to say hi in person? 🙌'} isMe={isMe} isAnswered={msg.is_answered} color="success" />;
        if (msg.question_key === 'instagram_share')
          return <UserBubble key={msg.id} text={msg.system_message} isMe={isMe} senderAlias={alias} />;
        return <QuestionChip key={msg.id} text={QUESTION_TEMPLATES[msg.question_key]?.text || msg.question_key} isMe={isMe} isAnswered={msg.is_answered} color={isMe ? 'primary' : 'default'} />;
      case 'answer':
        if (msg.question_key === 'instagram_share')
          return <UserBubble key={msg.id} text={msg.answer_value} isMe={isMe} senderAlias={alias} />;
        const qLabel = msg.question_key === 'meet_request' ? 'Meet up?' : (QUESTION_TEMPLATES[msg.question_key]?.text || msg.question_key);
        return <QABlock key={msg.id} questionText={qLabel} answerText={msg.answer_value} isMe={isMe} senderAlias={alias} />;
      default:
        return null;
    }
  };

  // ── Reset tray scroll to top on tab switch ──
  useEffect(() => {
    if (trayRef.current) trayRef.current.scrollTop = 0;
  }, [activeSection]);

  // ── EARLY RETURNS ──
  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: C.bgApp }}>
      <CircularProgress sx={{ color: C.yellow }} />
    </Box>
  );

  if (chatClosed) return (
    <Box sx={{ minHeight: '100vh', bgcolor: C.bgApp, p: 2 }}>
      <Paper elevation={3} sx={{ p: 3, maxWidth: 480, mx: 'auto', textAlign: 'center', bgcolor: C.bgCard, border: `1px solid ${C.border}` }}>
        <Typography variant="h6" sx={{ mb: 2, color: C.textPrimary }}>Chat Closed</Typography>
        <Button variant="outlined" onClick={() => navigate('/chats')}>← Back to Matches</Button>
      </Paper>
    </Box>
  );

  const { messages = [], otherUser, secondsRemaining = 0, isUnlocked, fsmState,
          myMessagesRemaining, otherMessagesRemaining, sectionsEnabled = {},
          pendingStructuredQ, pendingSocialQ, pendingMeetQ,
          identityStatus, identityActionChosen, myGender, isHetero } = serverState || {};

  const isExpired = secondsRemaining <= 0;
  const charsLeft = 150 - (freeText?.length || 0);

  // ── MAIN RENDER ──
  return (
    <Box sx={{ height: '100vh', overflow: 'hidden', bgcolor: C.bgApp, display: 'flex', flexDirection: 'column' }}>
      <Paper elevation={0} sx={{
        maxWidth: 480, mx: 'auto', width: '100%',
        display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden',
        bgcolor: C.bgApp,
        border: `1px solid ${C.border}`
      }}>

        <ChatHeader
          fromGame={fromGame}
          navigate={navigate}
          otherUser={otherUser}
          fsmState={fsmState}
          secondsRemaining={secondsRemaining}
          serverState={serverState}
          error={error}
          setError={setError}
          isUnlocked={isUnlocked}
          matchId={matchId}
          otherCheckin={serverState?.otherCheckin}
          onTriggerWeMet={triggerWeMet}
          onTriggerUnmatch={triggerUnmatch}
          onTriggerDeleteChat={triggerDeleteChat}
          onTriggerReport={triggerReport}
        />

        {/* MESSAGES */}
        <Box ref={messagesContainer} sx={{
          flex: 1, overflowY: 'auto',
          p: 2, bgcolor: C.bgApp,
        }}>
          {messages.length === 0 && <SystemMessage text="No messages yet. Ask a question to get started!" />}
          {messages.map(renderMessage)}
          {/* SPOT ME — inline right-aligned */}
          {ui.showSpotMeBtn && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1, px: 1 }}>
              <Paper elevation={0} sx={{
                p: 1.5, borderRadius: 2, maxWidth: 280,
                bgcolor: C.bgCard, border: `1px solid ${C.orange}`
              }}>
                <Typography variant="body2" sx={{ mb: 1, color: C.orange, fontWeight: 600 }}>
                  {ui.showTiedUpAlert
                    ? '⏳ You said “Give me 5 minutes”. Ready when you are!'
                    : 'Choose what you\'ll do!'}
                </Typography>
                <Button variant="contained" color="warning" fullWidth onClick={openSpotDialog}
                  sx={{ fontSize: '0.78rem', fontWeight: 600 }}>
                  Spot Me — show me what to do
                </Button>
              </Paper>
            </Box>
          )}
          {/* SPOTTING CONFIRM — inline right-aligned */}
          {ui.showSpottingConfirmCard && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1, px: 1 }}>
              <Paper elevation={0} sx={{
                p: 1.5, borderRadius: 2, maxWidth: 280,
                bgcolor: C.bgCard, border: `1px solid ${C.yellow}`
              }}>
                <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 1, color: C.yellow }}>
                  Did you spot them?
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Button size="small" variant="contained" color="success" fullWidth
                    sx={{ fontSize: '0.75rem' }} onClick={() => confirmSpotting(true)}>
                    ✅ Yes, I spotted you!
                  </Button>
                  <Button size="small" variant="outlined" fullWidth
                    sx={{ fontSize: '0.75rem' }} onClick={() => confirmSpotting(false)}>
                    🔄 Couldn't spot — retry?
                  </Button>
                </Box>
              </Paper>
            </Box>
          )}
          <div ref={messagesEnd} />
        </Box>

        <Divider sx={{ borderColor: C.border }} />

        {/* BUCKET DISPLAY */}
        {(serverState?.comfortBufferActive || serverState?.coordinationActive) && (
          <Box sx={{ pt: 1, flexShrink: 0 }}>
            <BucketDisplay myRemaining={myMessagesRemaining} otherRemaining={otherMessagesRemaining}
              phaseLabel={ui.phaseLabel} otherAlias={otherUser?.alias} />
          </Box>
        )}

        {/* PENDING STRUCTURED ANSWER CARD */}
        {pendingStructuredQ && (
          <Card sx={{
            mx: 2, mt: 1, p: 2, flexShrink: 0,
            bgcolor: C.bgCard, border: `1px solid ${C.yellow}`
          }}>
            <Typography variant="caption" sx={{ color: C.yellow, fontWeight: 'bold', display: 'block', mb: 0.5 }}>
              {otherUser?.alias} asked:
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1.5, color: C.textPrimary }}>
              {QUESTION_TEMPLATES[pendingStructuredQ.question_key]?.text}
            </Typography>
            <RadioGroup value={selectedAnswer} onChange={e => setSelectedAnswer(e.target.value)}>
              {(QUESTION_TEMPLATES[pendingStructuredQ.question_key]?.answers || []).map(a => (
                <FormControlLabel key={a} value={a} control={<Radio size="small" />} label={a}
                  sx={{ '& .MuiFormControlLabel-label': { color: C.textPrimary, fontSize: '0.9rem' } }} />
              ))}
            </RadioGroup>
            <Button variant="contained" size="small" fullWidth sx={{ mt: 1 }}
              onClick={() => sendAnswer(pendingStructuredQ.question_key, selectedAnswer)}
              disabled={!selectedAnswer}>
              Send Answer
            </Button>
          </Card>
        )}

        {/* PENDING MEET QUESTION CARD */}
        {ui.pendingMeetQ && (
          <Card sx={{
            mx: 2, mt: 1, p: 2, flexShrink: 0,
            bgcolor: C.bgCard, border: `1px solid ${C.teal}`
          }}>
            <Typography variant="caption" sx={{ color: C.teal, fontWeight: 'bold', display: 'block', mb: 0.5 }}>
              {otherUser?.alias} wants to meet!
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1.5, color: C.textPrimary }}>
              Would you like to say hi in person? 🙌
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Button variant="contained" color="success" size="small" onClick={() => respondToMeet('yes')}>
                Yes, let's meet! 🙌
              </Button>
              <Button variant="outlined" size="small" onClick={() => respondToMeet('maybe_later')}>
                Maybe later
              </Button>
              <Button variant="text" color="error" size="small" onClick={() => setNoThanksOpen(true)}>
                No thanks
              </Button>
            </Box>
          </Card>
        )}

        {/* PENDING SOCIAL QUESTION CARD */}
        {pendingSocialQ && !serverState?.alreadySharedHandle && (
          <Card sx={{
            mx: 2, mt: 1, p: 2, flexShrink: 0,
            bgcolor: C.bgCard, border: `1px solid ${C.teal}`
          }}>
            <Typography variant="caption" sx={{ color: C.teal, fontWeight: 'bold', display: 'block', mb: 0.5 }}>
              {otherUser?.alias} asked for your social:
            </Typography>
            <Typography variant="body2" sx={{ mb: 1.5, color: C.textPrimary }}>
              {pendingSocialQ.system_message}
            </Typography>
            {ui.canRespondWithHandle ? (
              <Box>
                <HandleInput handle={handle} setHandle={setHandle} platform={platform} setPlatform={setPlatform}
                  onSubmit={handleShareOnly} submitLabel="Go" />
                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                  <Button variant="outlined" size="small" onClick={() => handleDecline('not_comfortable')}>
                    Not comfortable
                  </Button>
                  <Button variant="outlined" size="small" onClick={() => handleDecline('not_active')}>
                    Not on socials
                  </Button>
                </Box>
              </Box>
            ) : (
              <Alert severity="success" icon={false}>✅ You already shared your handle</Alert>
            )}
          </Card>
        )}

        {/* DECISION GATE — removed, Meet tab handles this */}

        {/* FREE TEXT INPUT */}
        {ui.showFreeText && !isExpired && (
          <Box sx={{ mx: 2, mt: 1, flexShrink: 0 }}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
              <TextField multiline maxRows={3} fullWidth size="small" value={freeText}
                onChange={e => setFreeText(e.target.value)}
                onFocus={() => setInputFocused(true)}
                onBlur={() => setInputFocused(false)}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendFreeText(); } }}
                placeholder={ui.freeTextDisabled ? 'No messages remaining' : 'Type a message... (150 chars)'}
                inputProps={{ maxLength: 150 }}
                helperText={`${charsLeft} left`}
                disabled={ui.freeTextDisabled} />
              <Button variant="contained" size="small" onClick={sendFreeText} onMouseDown={e => e.preventDefault()}
                disabled={!freeText.trim() || freeText.length > 150 || ui.freeTextDisabled}
                sx={{ mb: 2.5, minWidth: 55 }}>
                Send
              </Button>
            </Box>
          </Box>
        )}

        {/* EXPIRED BANNER */}
        {isExpired && (
          <Box sx={{ px: 2, py: 1.5, textAlign: 'center', borderTop: `1px solid ${C.border}`, flexShrink: 0 }}>
            <Typography variant="body2" sx={{ color: C.textMuted }}>
              🔒 This chat has expired. Messages are read-only.
            </Typography>
          </Box>
        )}

        {/* SECTION NAV TABS + TRAY — sticky bottom zone */}
        {!isExpired && !inputFocused && <Box sx={{ flexShrink: 0, display: 'flex', flexDirection: 'column', bgcolor: C.bgCard }}>
        <Box sx={{ display: 'flex', borderTop: `1px solid ${C.border}`, flexShrink: 0, bgcolor: C.bgCard }}>
          {[
            { key: 'A', label: '💬 Ask',   enabled: sectionsEnabled.A },
            { key: 'B', label: '✨ Vibes', enabled: sectionsEnabled.B },
            { key: 'P', label: '🎮 Games', enabled: true },
            { key: 'D', label: '🤝 Meet',  enabled: sectionsEnabled.D }
          ].map(({ key, label, enabled }) => (
            <Button key={key}
              onClick={() => setActiveSection(activeSection === key ? null : key)}
              disabled={!enabled}
              sx={{
                flex: 1, py: 1.2, borderRadius: 0, fontSize: '0.7rem',
                color: activeSection === key ? C.yellow : enabled ? C.textMuted : C.textDisabled,
                borderBottom: activeSection === key ? `2px solid ${C.yellow}` : '2px solid transparent',
                fontWeight: activeSection === key ? 'bold' : 'normal',
                '&.Mui-disabled': { color: C.textDisabled }
              }}>
              {label}{!enabled && ' 🔒'}
            </Button>
          ))}
        </Box>
        <Box ref={trayRef} sx={{ overflowY: 'auto', height: ((playUiState === 'playing' || playUiState === 'ready') && activeSection === 'P') ? 'calc(100vh - 106px)' : '230px', flexShrink: 0, bgcolor: C.bgCard, transition: 'height 0.35s ease' }}>

        {/* ── SECTION A: Know Their Vibe ── */}
        {activeSection === 'A' && sectionsEnabled.A && (
          <Card sx={{ mx: 2, mb: 1, p: 2, flexShrink: 0, bgcolor: C.bgCard, border: `1px solid ${C.border}` }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1.5, color: C.textPrimary }}>
              💬 Ask
            </Typography>
            {Object.entries(QUESTION_TEMPLATES).map(([key, q]) => (
              <Button key={key} variant="outlined" size="small" fullWidth
                sx={{ textAlign: 'left', justifyContent: 'flex-start', mb: 0.8 }}
                onClick={() => sendQuestion(key)}
                disabled={ui.structuredQsBlocked}>
                {q.text}
              </Button>
            ))}
          </Card>
        )}

        {/* ── SECTION B: Quick Replies ── */}
        {activeSection === 'B' && sectionsEnabled.B && (
          <Card sx={{ mx: 2, mb: 1, p: 2, flexShrink: 0, bgcolor: C.bgCard, border: `1px solid ${C.border}` }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1.5, color: C.textPrimary }}>
              ✨ Vibes
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {QUICK_REPLIES.map(r => (
                <Button key={r} variant="outlined" size="small" onClick={() => sendQuickReply(r)}>{r}</Button>
              ))}
            </Box>
          </Card>
        )}

        {/* ── SECTION P: Play ── */}
        {activeSection === 'P' && (
          <Card sx={{ mx: 2, mb: 1, p: 2, flexShrink: 0, bgcolor: C.bgCard, border: `1px solid ${C.border}` }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5, color: C.textPrimary }}>
              🎮 Games
            </Typography>

            {(playUiState === 'idle' || playUiState === 'responding' || playUiState === 'done') && playUiState !== 'ready' && (
              <>
                <Typography variant="caption" sx={{ color: C.textMuted, display: 'block', mb: 1.5 }}>
                  Suggest a game:
                </Typography>
                {blocked && (
                  <Alert severity="warning" sx={{ mb: 1, fontSize: '0.78rem' }}>
                    Finish the current game first.
                  </Alert>
                )}
                {Object.entries(GAME_REGISTRY).map(([gameType, entry]) => (
                  <Button key={gameType} variant="outlined" size="small" fullWidth
                    disabled={proposing || playUiState === 'responding'}
                    sx={{ textAlign: 'left', justifyContent: 'flex-start', mb: 0.8 }}
                    onClick={() => proposeGameAndWait(gameType, entry.label)}>
                    {entry.label}
                  </Button>
                ))}
              </>
            )}

            {playUiState === 'waiting' && (
              <Typography variant="body2" sx={{ color: C.textMuted, textAlign: 'center', py: 2 }}>
                Waiting for them to respond…
              </Typography>
            )}

            {playUiState === 'declined' && (
              <>
                <Typography variant="body2" sx={{ color: C.textMuted, textAlign: 'center', py: 1, mb: 1 }}>
                  They're not up for a game right now
                </Typography>
                <Button variant="outlined" size="small" fullWidth onClick={resetToGameSelection}>
                  Try another game
                </Button>
              </>
            )}

            {(playUiState === 'playing' || playUiState === 'ready') && gameSessionId && (!exitedSessionRef.current || exitedSessionRef.current !== gameSessionId) && (() => {
              return (
                <ChatGameCard
                  key={gameSessionId}
                  sessionId={gameSessionId}
                  gameType={selectedGame}
                  myPlayerId={serverState?.myUserId}
                  otherPlayerId={serverState?.otherUserId}
                  onBack={() => resetToGameSelection(false, true)}
                  onRestart={handleGameRestart}
                />
              );
            })()}
          </Card>
        )}

        {/* ── SECTION C: Find Each Other ── */}
        {activeSection === 'C' && sectionsEnabled.C && (
          <Card sx={{ mx: 2, mb: 1, p: 2, flexShrink: 0, bgcolor: C.bgCard, border: `1px solid ${C.border}` }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1.5, color: C.textPrimary }}>
              👁️ Find Each Other
            </Typography>

            {ui.showInitiateSpottingBtn && (
              <Button variant="contained" color="warning" fullWidth size="small" sx={{ mb: 1 }} onClick={initiateSpotting}>
                {serverState?.identityReverseUnlocked && myGender === 'male'
                  ? '👁️ Ask them to spot you (your turn)'
                  : '👁️ Ask them to identify themselves'}
              </Button>
            )}
            {(identityStatus === 'woman_initiated' || identityStatus === 'man_initiated') && !ui.showInitiateSpottingBtn && !ui.showSpotMeBtn && !ui.showSpottingConfirmCard && (
              <Alert severity="info" sx={{ mb: 1 }} icon={false}>Spotting in progress…</Alert>
            )}

            <Divider sx={{ my: 1.5, borderColor: C.border }} />
            <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 1, color: C.textMuted }}>
              Social Handle Exchange
            </Typography>

            <SocialAskBar
              variant="early"
              ui={ui} serverState={serverState} pendingSocialQ={pendingSocialQ}
              handle={handle} setHandle={setHandle} platform={platform} setPlatform={setPlatform}
              handleShareAndAsk={handleShareAndAsk} handleAskOnly={handleAskOnly}
            />

            {ui.showSkipToChat && (
              <>
                <Divider sx={{ my: 1.5, borderColor: C.border }} />
                <Button variant="outlined" color="secondary" fullWidth size="small" onClick={skipToChat}>
                  Skip to brief chat!
                </Button>
              </>
            )}

            {ui.showBriefChatButton && (
              <>
                <Divider sx={{ my: 1.5, borderColor: C.border }} />
                <Button variant="outlined" color="secondary" fullWidth size="small" onClick={requestBuffer}>
                  💬 Start a brief chat (10 msgs each)
                </Button>
              </>
            )}
          </Card>
        )}

        {/* ── SECTION D: Say Hi IRL + Find Each Other ── */}
        {activeSection === 'D' && sectionsEnabled.D && (
          <Card sx={{ mx: 2, mb: 1, p: 2, flexShrink: 0, bgcolor: C.bgCard, border: `1px solid ${C.border}` }}>

            {myGender === 'female' && isHetero && identityStatus !== 'confirmed' && !serverState?.comfortBufferActive && !serverState?.coordinationActive && (
              <Typography variant="caption" sx={{ display: 'block', mb: 1.5, color: C.teal, textAlign: 'left', opacity: 0.85 }}>
                Only you can start — he responds
              </Typography>
            )}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>

              {ui.showInitiateSpottingBtn ? (
                <Button variant="outlined" fullWidth size="small"
                  sx={{ borderColor: C.yellow, color: C.yellow, justifyContent: 'flex-start', pl: 2 }}
                  onClick={initiateSpotting}>
                  How do I identify you here?
                </Button>
              ) : (isHetero && myGender === 'male' && !serverState?.coordinationActive && !serverState?.comfortBufferActive && identityStatus === 'not_started') ? (
                <Button variant="outlined" fullWidth size="small"
                  sx={{ justifyContent: 'flex-start', pl: 2, opacity: 0.4, pointerEvents: 'auto' }}
                  onClick={() => setManHint(true)}>
                  How do I identify you here?
                </Button>
              ) : null}
              {(identityStatus === 'woman_initiated' || identityStatus === 'man_initiated') && !ui.showInitiateSpottingBtn && !ui.showSpotMeBtn && !ui.showSpottingConfirmCard && (
                <Alert severity="info" icon={false}>Spotting in progress…</Alert>
              )}

              <SocialAskBar
                variant="late"
                ui={ui} serverState={serverState} pendingSocialQ={pendingSocialQ}
                handle={handle} setHandle={setHandle} platform={platform} setPlatform={setPlatform}
                handleShareAndAsk={handleShareAndAsk} handleAskOnly={handleAskOnly}
              />
              {isHetero && myGender === 'male' && !ui.showAskOnlyBtn && !serverState?.socialExchangePending && !serverState?.bothHandlesShared && !pendingSocialQ && (
                <Button variant="outlined" fullWidth size="small"
                  sx={{ justifyContent: 'flex-start', pl: 2, opacity: 0.4, pointerEvents: 'auto' }}
                  onClick={() => setManHint(true)}>
                  Can I have your social or Insta ID?
                </Button>
              )}

              {ui.showSkipToChat && (
                <Button variant="outlined" fullWidth size="small"
                  sx={{ borderColor: C.yellow, color: C.yellow, justifyContent: 'flex-start', pl: 2 }}
                  onClick={skipToChat}>
                  Skip to brief chat!
                </Button>
              )}

              {ui.showBriefChatButton ? (
                <Button variant="outlined" fullWidth size="small"
                  sx={{ borderColor: C.yellow, color: C.yellow, justifyContent: 'flex-start', pl: 2 }}
                  onClick={requestBuffer}>
                  Start a brief chat (10 msgs each)
                </Button>
              ) : (isHetero && myGender === 'male' && !serverState?.comfortBufferUsed && !serverState?.comfortBufferActive && !serverState?.coordinationActive) ? (
                <Button variant="outlined" fullWidth size="small"
                  sx={{ justifyContent: 'flex-start', pl: 2, opacity: 0.4, pointerEvents: 'auto' }}
                  onClick={() => setManHint(true)}>
                  Start a brief chat (10 msgs each)
                </Button>
              ) : null}

              {ui.meetIsCoordinating ? (
                <Alert severity="info">Coordinating — use the text box above to plan details.</Alert>
              ) : ui.pendingMeetQ ? (
                <Alert severity="info" icon={false}>They sent you a meet request — respond in the card above ↑</Alert>
              ) : ui.iSentMeetQ ? (
                <Alert severity="info" icon={false}>Meet request sent — waiting for their answer...</Alert>
              ) : (
                <Button variant="outlined" fullWidth
                  sx={{ borderColor: C.yellow, color: C.yellow, justifyContent: 'flex-start', pl: 2,
                    opacity: (isHetero && myGender === 'male' && !serverState?.decisionGateAvailable) ? 0.4 : 1,
                    pointerEvents: 'auto' }}
                  disabled={ui.structuredQsBlocked}
                  onClick={isHetero && myGender === 'male' && !serverState?.decisionGateAvailable ? () => setManHint(true) : askToMeet}>
                  Say Hi in person?
                </Button>
              )}

            </Box>

            {ui.meetIsCoordinating && (
              <>
                <Divider sx={{ my: 1.5, borderColor: C.border }} />
                <Typography variant="body2" sx={{ mb: 1, color: C.textMuted, fontWeight: 'bold' }}>
                  Did you guys meet?
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button variant="outlined" color="success" size="small" sx={{ flex: 1 }} onClick={confirmMeeting}>
                    Yes
                  </Button>
                  <Button variant="outlined" size="small" sx={{ flex: 1 }}>
                    No
                  </Button>
                </Box>
              </>
            )}
          </Card>
        )}

        <div ref={sectionEnd} />
        </Box>
        </Box>}

      </Paper>

      {/* SPOT ME DIALOG */}
      <Dialog open={showSpotDialog} onClose={() => setShowSpotDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ color: C.textPrimary }}>Help them spot you</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, color: C.textMuted }}>
            Pick something quick. You have about 15 seconds!
          </Typography>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Choose an action</InputLabel>
            <Select value={identityAction} onChange={e => setIdentityAction(e.target.value)} label="Choose an action">
              {IDENTITY_ACTIONS.map(a => <MenuItem key={a} value={a}>I'll {a}</MenuItem>)}
            </Select>
          </FormControl>
          <Button variant="contained" fullWidth sx={{ mb: 1 }}
            onClick={() => submitSpotAction(identityAction)} disabled={!identityAction}>
            Doing it now (15 sec)
          </Button>
          <Button variant="outlined" fullWidth sx={{ mb: 1 }} onClick={() => submitSpotAction('tied_up')}>
            Give me 5 minutes
          </Button>
          <Button variant="text" color="error" fullWidth onClick={() => submitSpotAction('prefer_not')}>
            Prefer not to continue
          </Button>
        </DialogContent>
      </Dialog>

    <Dialog
        open={noThanksOpen}
        onClose={() => setNoThanksOpen(false)}
        PaperProps={{ sx: { bgcolor: '#1E1E1E', border: '1px solid #333', borderRadius: 2, minWidth: 280 } }}
      >
        <DialogTitle sx={{ color: '#EFEFEF', fontSize: 16, pb: 1 }}>Decline Meet</DialogTitle>
        <DialogContent>
          <Box sx={{ color: '#AAAAAA', fontSize: 14 }}>
            This will unmatch you with {otherUser?.alias}. Do you wish to continue?
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2, gap: 1 }}>
          <Button onClick={() => setNoThanksOpen(false)} sx={{ color: '#AAAAAA', fontSize: 13 }}>Cancel</Button>
          <Button onClick={() => { setNoThanksOpen(false); respondToMeet('no_thanks'); }}
            sx={{ color: '#E53935', fontSize: 13, fontWeight: 'bold' }}>Yes, unmatch</Button>
        </DialogActions>
      </Dialog>
    <Snackbar
        open={manHint}
        autoHideDuration={3000}
        onClose={() => setManHint(false)}
        message="She must initiate first"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
}

export default ChatConversation;